import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ChevronLeft } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../../layout/ListToolbar';
import usePotvrzeni from '../../ui/usePotvrzeni';
import { SECTION_COLOR, SECTION_BG, hlavicky, formatCasListu } from '../komunikace';
import { DRUHY, STAVY, termin, kc, cestaAkce, POPISKY_AKCI, KLADNE_AKCE } from './zadostiUi';
import NovaZadost from './NovaZadost';
import UpravitNepritomnost from '../../notifikace/UpravitNepritomnost';
import ZpetTlacitko from '../../ui/ZpetTlacitko';
import PrilohyMrizka from '../prilohy/PrilohyMrizka';
import PrilohyVyber from '../prilohy/PrilohyVyber';

/**
 * Komunikace → Žádosti (etapa 4). Norma §1: seznam · detail · třetí sloupec.
 *
 * Jeden seznam pro volno, nemoc, výměnu směny, zálohu, nákup i „jiné" —
 * dřív byla žádost o volno v Přehledu, výměna v kalendáři a zbytek v chatu
 * nebo po telefonu. Vedoucí tu má „Ke schválení", zaměstnanec „Moje".
 * Třetí sloupec u volna ukazuje dopad (které směny tím odpadají).
 */
const POHLEDY = [
    { id: 'ke-schvaleni', label: 'Ke schválení', upozorneni: true, patri: p => p.akce.some(a => !['zrusit', 'stornovat'].includes(a)) },
    { id: 'moje', label: 'Moje', patri: p => p.moje },
    { id: 'cekajici', label: 'Čekající', patri: p => p.stav === 'ceka' },
    { id: 'vyrizene', label: 'Vyřízené', patri: p => p.stav !== 'ceka' },
];

export default function Zadosti({ user, isDark, isMobile, cardStyle, inputStyle, mutedColor, initialKlic = null, rezervaDole = null }) {
    const [data, setData] = useState(null);
    const [pohled, setPohled] = useState(null);
    const [hledani, setHledani] = useState('');
    const [vybranyKlic, setVybranyKlic] = useState(initialKlic);
    const [nova, setNova] = useState(false);
    const [odpoved, setOdpoved] = useState('');
    const [prilohyVyrizeni, setPrilohyVyrizeni] = useState([]);
    const [nahravam, setNahravam] = useState(false);
    const [bezi, setBezi] = useState(null);
    // Nepřítomnost, kterou rozhodující před schválením opravuje (typicky hodiny).
    const [upravujeId, setUpravujeId] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [dopad, setDopad] = useState(null);
    const [zaskokHlaska, setZaskokHlaska] = useState({});   // smena_id → text
    const [potvrdit, potvrzeniDialog] = usePotvrzeni(isDark);

    useEffect(() => { if (initialKlic) setVybranyKlic(initialKlic); }, [initialKlic]);

    const nacti = useCallback(() => {
        fetch('/api/zadosti').then(r => (r.ok ? r.json() : null)).then(d => { if (d) setData(d); }).catch(() => {});
    }, []);
    useEffect(() => { nacti(); }, [nacti]);

    const polozky = data?.polozky || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, polozky.filter(p.patri).length])), [polozky]);
    const viditelnePohledy = POHLEDY.filter(p => p.id !== 'ke-schvaleni' || data?.rozhoduji);
    // Výchozí pohled: vedoucí s něčím ke schválení tam, jinak moje
    const aktivniId = pohled || (data?.rozhoduji && pocty['ke-schvaleni'] > 0 ? 'ke-schvaleni' : 'moje');
    const aktivniPohled = POHLEDY.find(p => p.id === aktivniId) || POHLEDY[1];
    const hledat = hledani.trim().toLowerCase();
    const seznam = polozky.filter(p => aktivniPohled.patri(p) && (!hledat || `${p.nazev} ${p.zadatel} ${p.popis || ''}`.toLowerCase().includes(hledat)));
    const vybrana = polozky.find(p => p.klic === vybranyKlic) || null;

    useEffect(() => { setOdpoved(''); setPrilohyVyrizeni([]); setChyba(null); }, [vybranyKlic]);

    // Dopad volna / nemoci — které směny v tom termínu má
    useEffect(() => {
        setDopad(null);
        if (!vybrana || !['volno', 'nemoc', 'vymena', 'predani'].includes(vybrana.druh) || !vybrana.zamestnanec_id || !vybrana.od) return;
        fetch(`/api/zadosti/dopad?zamestnanec_id=${vybrana.zamestnanec_id}&od=${vybrana.od}&do=${vybrana.do || vybrana.od}`)
            .then(r => (r.ok ? r.json() : [])).then(d => setDopad(Array.isArray(d) ? d : [])).catch(() => setDopad([]));
    }, [vybrana?.klic]); // eslint-disable-line react-hooks/exhaustive-deps

    const provest = async (akce) => {
        if (!vybrana) return;
        if (['zamitnout', 'admin_zamitnout', 'odmitnout', 'zrusit', 'stornovat'].includes(akce)) {
            const otazka = { zamitnout: 'Zamítnout žádost?', admin_zamitnout: 'Zamítnout výměnu?', odmitnout: 'Odmítnout výměnu?', zrusit: 'Stáhnout žádost?', stornovat: 'Stáhnout návrh výměny?' }[akce];
            if (!(await potvrdit(otazka, { potvrdit: POPISKY_AKCI[akce] }))) return;
        }
        const { url, metoda } = cestaAkce(vybrana, akce);
        setBezi(akce);
        setChyba(null);
        try {
            const res = await fetch(url, {
                method: metoda, headers: hlavicky(),
                body: vybrana.zdroj === 'zadost' && ['schvalit', 'zamitnout'].includes(akce) ? JSON.stringify({ odpoved: odpoved.trim() || null, prilohy: prilohyVyrizeni.map(x => x.id) }) : undefined,
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(d.error || d.message || 'Nepodařilo se.'); return; }
            setPrilohyVyrizeni([]);
            nacti();
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
        } finally {
            setBezi(null);
        }
    };

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    const stavChip = (p) => {
        const text = p.podstav === 'ceka_na_kolegu' ? 'Čeká na kolegu' : p.podstav === 'ceka_na_vedouciho' ? 'Čeká na vedoucího' : STAVY[p.stav];
        const zvyraznit = p.stav === 'ceka';
        const cervene = p.stav === 'zamitnuto';
        return (
            <span className="shrink-0 text-[11px] font-semibold rounded-full" style={{
                padding: '2px 8px',
                background: zvyraznit ? SECTION_BG : 'transparent',
                color: cervene ? '#F87171' : zvyraznit ? SECTION_COLOR : muted,
                border: `1px solid ${zvyraznit ? 'transparent' : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`,
            }}>{text}</span>
        );
    };

    const radek = (p, siroky) => {
        const { Icon } = DRUHY[p.druh] || DRUHY.jine;
        const aktivni = p.klic === vybranyKlic;
        const kdo = p.moje ? null : p.zadatel;
        const podtitul = [kdo, termin(p.od, p.do), kc(p.castka)].filter(Boolean).join(' · ');
        return (
            <button key={p.klic} type="button" onClick={() => setVybranyKlic(p.klic)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 60, background: aktivni ? SECTION_BG : 'transparent', boxShadow: aktivni ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={17} />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{p.nazev}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', color: muted, fontSize: isMobile ? 13 : 12 }}>{podtitul || formatCasListu(p.vytvoreno_at)}</p>
                </div>
                {siroky && <span className="shrink-0 text-xs" style={{ width: 90, color: muted }}>{formatCasListu(p.vytvoreno_at)}</span>}
                {stavChip(p)}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat žádost, kolegu…', placeholderKratky: 'Hledat' }}
                akce={user?.zamestnanec_id ? [{ id: 'nova', label: 'Nová žádost', Icon: Plus, primarni: true, onClick: () => setNova(true) }] : []}
                pohledy={!siroky && !isMobile ? viditelnePohledy.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? SECTION_COLOR : undefined })) : null}
                pohledAktivni={aktivniPohled.id}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : viditelnePohledy.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={aktivniPohled.id === p.id}
                        upozorneni={p.upozorneni && pocty[p.id] > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje' : aktivniPohled.id === 'ke-schvaleni' ? 'Nic nečeká na tvoje rozhodnutí.' : aktivniPohled.id === 'moje' ? 'Zatím žádná žádost. Volno, zálohu nebo výměnu směny založíš přes „Nová žádost".' : 'Nic tu není.'}
                    </p>
                )}
                {seznam.map(p => radek(p, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const najitZaskok = async (s) => {
        setZaskokHlaska(prev => ({ ...prev, [s.id]: '…' }));
        const res = await fetch('/api/zaskoky', { method: 'POST', headers: hlavicky(), body: JSON.stringify({ smena_id: s.id, duvod: vybrana?.druh === 'nemoc' ? 'nemoc' : 'volno' }) });
        const d = await res.json().catch(() => ({}));
        setZaskokHlaska(prev => ({ ...prev, [s.id]: res.ok ? (d.stav === 'obsazeno' ? 'Obsazeno' : d.kandidat ? `Bere ${d.kandidat}` : 'Nabídnuto v kanálu') : (d.error || 'Nepodařilo se') }));
    };

    const dopadObsah = (kompaktni) => {
        if (!vybrana || dopad === null) return null;
        const lzeZaskok = ['volno', 'nemoc'].includes(vybrana.druh) && vybrana.stav !== 'zamitnuto' && vybrana.stav !== 'zruseno';
        return (
            <div style={kompaktni ? { marginTop: 18 } : null}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' }}>
                    {vybrana.druh === 'vymena' || vybrana.druh === 'predani' ? 'Směna' : 'Směny v tom termínu'} · {dopad.length}
                </p>
                {dopad.length === 0 && <p className="text-xs" style={{ color: muted }}>V tom termínu nemá žádnou směnu.</p>}
                {dopad.map(s => (
                    <div key={s.id} style={{ padding: '6px 0', borderBottom: `1px solid ${cara}` }}>
                        <div className="flex justify-between text-[13px]" style={{ alignItems: 'center' }}>
                            <span style={{ color: 'var(--text-primary)' }}>{new Date(s.datum).toLocaleDateString('cs', { weekday: 'short', day: 'numeric', month: 'numeric' })}</span>
                            <span className="tabular-nums" style={{ color: muted }}>{s.cas_od}–{s.cas_do}{s.nazev ? ` · ${s.nazev}` : ''}</span>
                        </div>
                        {lzeZaskok && (
                            zaskokHlaska[s.id]
                                ? <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>{zaskokHlaska[s.id]}</p>
                                : <button type="button" onClick={() => najitZaskok(s)} className="text-xs font-semibold" style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, color: SECTION_COLOR, cursor: 'pointer' }}>Najít záskok →</button>
                        )}
                    </div>
                ))}
                {vybrana.stav === 'ceka' && vybrana.druh === 'volno' && dopad.length > 0 && (
                    <p className="text-xs" style={{ color: muted, marginTop: 8 }}>Po schválení bude potřeba tyhle směny obsadit jinak.</p>
                )}
            </div>
        );
    };

    const detailPanel = () => {
        const p = vybrana;
        const tlac = (akce) => {
            const kladna = KLADNE_AKCE.includes(akce);
            return (
                <button key={akce} type="button" onClick={() => provest(akce)} disabled={!!bezi || nahravam} className="rounded-lg font-semibold"
                    style={{ height: isMobile ? 46 : 38, padding: '0 16px', fontSize: 14, cursor: bezi ? 'wait' : 'pointer', flex: isMobile ? 1 : undefined,
                        background: kladna ? SECTION_COLOR : 'transparent', color: kladna ? '#fff' : (['zamitnout', 'admin_zamitnout'].includes(akce) ? '#F87171' : 'var(--text-primary)'),
                        border: kladna ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                    {bezi === akce ? '…' : POPISKY_AKCI[akce]}
                </button>
            );
        };
        const pole = (label, hodnota) => (hodnota ? (
            <div className="flex justify-between gap-4 text-[13.5px]" style={{ minHeight: 36, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{label}</span><span className="text-right" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{hodnota}</span>
            </div>
        ) : null);

        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '10px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && (
                        <ZpetTlacitko onClick={() => setVybranyKlic(null)} popis="Zpět na žádosti" sklo isDark={isDark} style={{ marginBottom: 10 }} />
                    )}
                    {p ? (
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, color: 'var(--text-primary)', margin: 0 }}>{p.nazev}</h2>
                                <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>{p.moje ? 'Moje žádost' : p.zadatel} · podáno {formatCasListu(p.vytvoreno_at)}</p>
                            </div>
                            {stavChip(p)}
                        </div>
                    ) : <p className="text-sm" style={{ color: muted }}>Žádost už v seznamu není.</p>}
                </div>
                {p && (
                    <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                        {pole('Druh', DRUHY[p.druh]?.nazev)}
                        {pole('Kdo', p.zadatel)}
                        {pole(p.druh === 'zaloha' ? 'Kdy' : 'Termín', termin(p.od, p.do))}
                        {pole('Částka', kc(p.castka))}
                        {pole('Hodiny', p.hodiny != null ? `${Number(p.hodiny).toLocaleString('cs-CZ')} h/den` : null)}
                        {p.popis && <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary)', margin: '14px 0 0', lineHeight: 1.55 }}>{p.popis}</p>}
                        {(p.prilohy || []).some(x => x.ucel !== 'vyrizeni') && (
                            <div style={{ marginTop: 14 }}>
                                <PrilohyMrizka prilohy={p.prilohy.filter(x => x.ucel !== 'vyrizeni')} isDark={isDark} velikostNahledu={isMobile ? 96 : 110} />
                            </div>
                        )}
                        {p.stav !== 'ceka' && (p.vyresil || p.odpoved || (p.prilohy || []).some(x => x.ucel === 'vyrizeni')) && (
                            <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                                <p className="text-xs" style={{ color: muted, margin: 0 }}>{STAVY[p.stav]}{p.vyresil ? ` · ${p.vyresil}` : ''}{p.vyreseno_at ? ` · ${formatCasListu(p.vyreseno_at)}` : ''}</p>
                                {p.odpoved && <p className="text-sm" style={{ color: 'var(--text-primary)', margin: '4px 0 0' }}>{p.odpoved}</p>}
                                {(p.prilohy || []).some(x => x.ucel === 'vyrizeni') && (
                                    <div style={{ marginTop: 8 }}><PrilohyMrizka prilohy={p.prilohy.filter(x => x.ucel === 'vyrizeni')} isDark={isDark} velikostNahledu={80} /></div>
                                )}
                            </div>
                        )}

                        {isMobile && dopadObsah(true)}

                        {p.akce.length > 0 && (
                            <div style={{ marginTop: 20 }}>
                                {p.zdroj === 'zadost' && p.akce.includes('schvalit') && (
                                    <textarea value={odpoved} onChange={e => setOdpoved(e.target.value)} rows={2} placeholder="Poznámka pro žadatele (nepovinné)"
                                        style={{ width: '100%', borderRadius: 8, padding: '10px 12px', outline: 'none', fontSize: 14, marginBottom: 8, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)' }} />
                                )}
                                {p.zdroj === 'zadost' && p.akce.includes('schvalit') && (
                                    <div style={{ marginBottom: 12 }}>
                                        <PrilohyVyber typ="zadost" prilohy={prilohyVyrizeni} onChange={setPrilohyVyrizeni} onNahravani={setNahravam} isDark={isDark} popisek={false} kompaktni />
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {p.akce.map(tlac)}
                                    {p.zdroj === 'nepritomnost' && p.akce.includes('schvalit') && (
                                        <button type="button" onClick={() => setUpravujeId(p.id)} disabled={!!bezi} className="rounded-lg font-semibold"
                                            style={{ height: isMobile ? 46 : 38, padding: '0 16px', fontSize: 14, cursor: 'pointer', flex: isMobile ? 1 : undefined,
                                                background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                                            Upravit
                                        </button>
                                    )}
                                </div>
                                {chyba && <p className="text-xs" style={{ color: '#EF4444', marginTop: 8 }}>{chyba}</p>}
                            </div>
                        )}
                        {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                    </div>
                )}
            </div>
        );
    };

    const panel = () => {
        if (vybrana && dopad !== null) return <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>{dopadObsah(false)}</div>;
        const ceka = polozky.filter(p => p.moje && p.stav === 'ceka').length;
        const radekP = (l, h, zvyr) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: zvyr ? SECTION_COLOR : 'var(--text-primary)' }}>{h}</b>
            </div>
        );
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' }}>Žádosti</p>
                {data?.rozhoduji && radekP('Čeká na moje rozhodnutí', pocty['ke-schvaleni'] || 0, (pocty['ke-schvaleni'] || 0) > 0)}
                {radekP('Moje čekající', ceka)}
                <p className="text-xs" style={{ color: muted, marginTop: 12 }}>
                    Volno, výměnu směny, zálohu i nákup schvaluje nadřízený, zálohu vedení. Nemoc se jen hlásí.
                </p>
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout
                isMobile={isMobile}
                vybrano={!!vybranyKlic}
                mobilRezim
                mobilPanel={false}
                panelVzdy
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={seznamPanel}
                detail={detailPanel}
                panel={panel}
            />
            {nova && <NovaZadost user={user} isDark={isDark} onClose={() => setNova(false)} onHotovo={() => { setNova(false); nacti(); setPohled('moje'); }} />}
            {upravujeId && <UpravitNepritomnost nedostupnostId={upravujeId} isDark={isDark} onClose={() => setUpravujeId(null)} onHotovo={() => { setUpravujeId(null); nacti(); window.dispatchEvent(new Event('notifikace-changed')); }} />}
            {potvrzeniDialog}
        </div>
    );
}
